/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { ChatWidget } from "../custom-features/EphemeralChat/ChatWidget";
import { RoomContext } from "@livekit/components-react";
import { RoomEvent, type Participant } from "livekit-client";

import { type MatrixClient, type Room as MatrixRoom } from "matrix-js-sdk";
import {
  type FC,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import useMeasure from "react-use-measure";
import { type MatrixRTCSession } from "matrix-js-sdk/lib/matrixrtc";
import classNames from "classnames";
import { map } from "rxjs";
import { useObservable } from "observable-hooks";
import { logger as rootLogger } from "matrix-js-sdk/lib/logger";
import { useTranslation } from "react-i18next";

import { Header, LeftNav, RightNav, RoomHeaderInfo } from "../Header";
import { HeaderStyle, useUrlParams } from "../UrlParams";
import { useCallViewKeyboardShortcuts } from "../useCallViewKeyboardShortcuts";
import { widget } from "../widget";
import styles from "./InCallView.module.css";
import { GridTile } from "../tile/GridTile";
import { SettingsModal, defaultSettingsTab } from "../settings/SettingsModal";
import { useRageshakeRequestModal } from "../settings/submit-rageshake";
import { RageshakeRequestModal } from "./RageshakeRequestModal";
import { useWakeLock } from "../useWakeLock";
import { useMergedRefs } from "../useMergedRefs";
import { type MuteStates } from "../state/MuteStates";
import { type MatrixInfo } from "./VideoPreview";
import { InviteButton } from "../button/InviteButton";
import {
  type CallViewModel,
  createCallViewModel$,
} from "../state/CallViewModel/CallViewModel.ts";
import { Grid, type TileProps } from "../grid/Grid";
import { SpotlightTile } from "../tile/SpotlightTile";
import { type EncryptionSystem } from "../e2ee/sharedKeyManagement";
import { E2eeType } from "../e2ee/e2eeType";
import { makeGridLayout } from "../grid/GridLayout";
import { type CallLayoutOutputs } from "../grid/CallLayout";
import { makeOneOnOneLandscapeLayout } from "../grid/OneOnOneLandscapeLayout";
import { makeOneOnOnePortraitLayout } from "../grid/OneOnOnePortraitLayout";
import { makeSpotlightExpandedLayout } from "../grid/SpotlightExpandedLayout";
import { makeSpotlightLandscapeLayout } from "../grid/SpotlightLandscapeLayout";
import { makeSpotlightPortraitLayout } from "../grid/SpotlightPortraitLayout";
import { GridTileViewModel, type TileViewModel } from "../state/TileViewModel";
import {
  ReactionsSenderProvider,
  useReactionsSender,
} from "../reactions/useReactionsSender";
import { ReactionsAudioRenderer } from "./ReactionAudioRenderer";
import { ReactionsOverlay } from "./ReactionsOverlay";
import { CallEventAudioRenderer } from "./CallEventAudioRenderer";
import { matrixRTCMode as matrixRTCModeSetting } from "../settings/settings";
import { ReactionsReader } from "../reactions/ReactionsReader";
import { LivekitRoomAudioRenderer } from "../livekit/MatrixAudioRenderer.tsx";
import { muteAllAudio$ } from "../state/MuteAllAudioModel.ts";
import { useMediaDevices } from "../MediaDevicesContext.ts";
import { EarpieceOverlay } from "./EarpieceOverlay.tsx";
import {
  useAppBarHidden,
  useAppBarSecondaryButton,
  useAppBarSubtitle,
} from "../AppBar.tsx";
import { useBehavior } from "../useBehavior.ts";
import { constant } from "../state/Behavior.ts";
import { Toast } from "../Toast.tsx";
import overlayStyles from "../Overlay.module.css";
import { useTrackProcessorObservable$ } from "../livekit/TrackProcessorContext.tsx";
import { type Layout } from "../state/layout-types.ts";
import { ObservableScope } from "../state/ObservableScope.ts";
import { CallFooter, type FooterSnapshot } from "../components/CallFooter.tsx";
import { SettingsIconButton } from "../button/Button.tsx";
import { createCallFooterViewModel } from "../components/CallFooterViewModel.tsx";
import { type ViewModel } from "../state/ViewModel.ts";
import { RingingStatus } from "../tile/RingingStatus.tsx";
import { RingingAudioRenderer } from "./RingingAudioRenderer.tsx";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "";

const AVATAR_COLORS = [
  "#5c6bc0",
  "#26a69a",
  "#ec407a",
  "#ab47bc",
  "#42a5f5",
  "#ffa726",
  "#66bb6a",
  "#ef5350",
];

function getAvatarColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface ExtendedVideoElement extends HTMLVideoElement {
  autoPictureInPicture?: boolean;
  webkitSupportsPresentationMode?: (mode: string) => boolean;
  webkitSetPresentationMode?: (mode: string) => void;
  webkitPresentationMode?: string;
}

interface ExtendedMediaSession extends MediaSession {
  setMicrophoneActive?: (active: boolean) => void;
  setCameraActive?: (active: boolean) => void;
}

declare module "react" {
  interface CSSProperties {
    "--call-view-safe-area-inset-top"?: string;
    "--call-view-safe-area-inset-bottom"?: string;
  }
}

export interface ActiveCallProps extends Omit<
  InCallViewProps,
  "vm" | "livekitRoom" | "connState" | "footerVm"
> {
  e2eeSystem: EncryptionSystem;
  onLeft: (
    reason: "user" | "timeout" | "decline" | "allOthersLeft" | "error",
  ) => void;
}

export const ActiveCall: FC<ActiveCallProps> = (props) => {
  const [vm, setVm] = useState<CallViewModel | null>(null);
  const [footerVm, setFooterVm] = useState<ViewModel<FooterSnapshot> | null>(
    null,
  );
  const urlParams = useUrlParams();
  const mediaDevices = useMediaDevices();
  const trackProcessorState$ = useTrackProcessorObservable$();
  useEffect(() => {
    rootLogger.info("START CALL VIEW SCOPE");
    const scope = new ObservableScope();
    const reactionsReader = new ReactionsReader(scope, props.rtcSession);
    const { autoLeaveWhenOthersLeft, waitForCallPickup, sendNotificationType } =
      urlParams;

    const vm = createCallViewModel$(
      scope,
      props.rtcSession,
      props.matrixRoom,
      mediaDevices,
      props.muteStates,
      {
        encryptionSystem: props.e2eeSystem,
        autoLeaveWhenOthersLeft,
        waitForCallPickup: waitForCallPickup && sendNotificationType === "ring",
        matrixRTCMode$: matrixRTCModeSetting.value$,
      },
      reactionsReader.raisedHands$,
      reactionsReader.reactions$,
      scope.behavior(trackProcessorState$),
    );

    vm.join();
    setVm(vm);

    vm.leave$.pipe(scope.bind()).subscribe(props.onLeft);

    // --- МГНОВЕННЫЙ ВЫХОД ПРИ ЗАКРЫТИИ ВКЛАДКИ (CHROME + SAFARI) ---
    let hasLeft = false;
    const handleTabClose = (): void => {
      if (hasLeft) return;
      hasLeft = true;

      const originalFetch = window.fetch.bind(window);
      window.fetch = (
        input: RequestInfo | URL,
        init?: RequestInit,
      ): Promise<Response> => {
        return originalFetch(input, { ...init, keepalive: true });
      };

      try {
        void props.rtcSession.leaveRoomSession(1000);
      } catch (e) {
        console.error("Ошибка leaveRoomSession:", e);
      }

      try {
        const userId = props.client.getUserId();
        const deviceId = props.client.getDeviceId();
        const accessToken = props.client.getAccessToken();
        const baseUrl = props.client.baseUrl;
        const roomId = props.matrixRoom.roomId;

        if (userId && accessToken && baseUrl && roomId) {
          const eventTypes = [
            "org.matrix.msc3401.call.member",
            "m.call.member",
          ];

          for (const eventType of eventTypes) {
            const stateEvents =
              props.matrixRoom.currentState.getStateEvents(eventType);

            for (const ev of stateEvents) {
              const stateKey = ev.getStateKey();
              const content = ev.getContent();
              if (!stateKey || !content || Object.keys(content).length === 0) {
                continue;
              }

              const isMyEvent =
                ev.getSender() === userId ||
                stateKey === userId ||
                stateKey.includes(userId);

              if (isMyEvent) {
                const isLegacy = Array.isArray(content.memberships);
                const remaining = isLegacy
                  ? content.memberships.filter(
                      (m: { device_id?: string }) => m.device_id !== deviceId,
                    )
                  : [];
                const nextContent =
                  isLegacy && remaining.length > 0
                    ? { memberships: remaining }
                    : {};

                const url = `${baseUrl.replace(/\/$/, "")}/_matrix/client/v3/rooms/${encodeURIComponent(roomId)}/state/${encodeURIComponent(eventType)}/${encodeURIComponent(stateKey)}`;
                originalFetch(url, {
                  method: "PUT",
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(nextContent),
                  keepalive: true,
                }).catch(() => {});
              }
            }
          }
        }
      } catch (e) {
        console.error("Ошибка прямой очистки стейта Matrix:", e);
      }

      try {
        const connList =
          (
            vm.allConnections$ as unknown as {
              get?: () => {
                getConnections: () => Array<{
                  livekitRoom?: { disconnect: (stopTracks?: boolean) => void };
                }>;
              };
            }
          ).get?.() ??
          (
            vm.allConnections$ as unknown as {
              value?: {
                getConnections: () => Array<{
                  livekitRoom?: { disconnect: (stopTracks?: boolean) => void };
                }>;
              };
            }
          ).value;

        const connections = connList?.getConnections?.() ?? [];
        for (const conn of connections) {
          conn.livekitRoom?.disconnect(true);
        }
      } catch (e) {
        console.error("Ошибка отключения LiveKit:", e);
      }

      scope.end();
    };

    window.addEventListener("beforeunload", handleTabClose);
    window.addEventListener("pagehide", handleTabClose);

    return (): void => {
      window.removeEventListener("beforeunload", handleTabClose);
      window.removeEventListener("pagehide", handleTabClose);
      scope.end();
    };
  }, [
    props.rtcSession,
    props.matrixRoom,
    props.muteStates,
    props.e2eeSystem,
    props.onLeft,
    urlParams,
    mediaDevices,
    trackProcessorState$,
    props.client,
  ]);

  useEffect(() => {
    if (vm === null) return;

    const scope = new ObservableScope();
    const footerVm = createCallFooterViewModel(
      scope,
      vm,
      props.muteStates,
      mediaDevices,
      `${props.client.getUserId()}:${props.client.getDeviceId()}`,
    );
    setFooterVm(footerVm);

    return (): void => {
      scope.end();
    };
  }, [
    props.rtcSession,
    props.matrixRoom,
    props.muteStates,
    props.e2eeSystem,
    props.onLeft,
    urlParams,
    mediaDevices,
    trackProcessorState$,
    props.client,
    vm,
  ]);

  if (vm === null) return null;
  if (footerVm === null) return null;

  return (
    <ReactionsSenderProvider vm={vm} rtcSession={props.rtcSession}>
      <InCallView {...props} vm={vm} footerVm={footerVm} />
    </ReactionsSenderProvider>
  );
};

export interface InCallViewProps {
  client: MatrixClient;
  vm: CallViewModel;
  footerVm: ViewModel<FooterSnapshot>;
  matrixInfo: MatrixInfo;
  rtcSession: MatrixRTCSession;
  matrixRoom: MatrixRoom;
  muteStates: MuteStates;
  onShareClick: (() => void) | null;
}

export const InCallView: FC<InCallViewProps> = ({
  client,
  vm,
  footerVm,
  matrixInfo,
  matrixRoom,
  muteStates,
  onShareClick,
}) => {
  const logger = rootLogger.getChild("[InCallView]");
  const { t, i18n } = useTranslation();
  const { sendReaction, toggleRaisedHand } = useReactionsSender();

  // --- АВТОМАТИЧЕСКИЙ ПЕРЕВОД КАСТОМНЫХ КНОПОК И PiP ПО ЯЗЫКУ СИСТЕМЫ / ПРИЛОЖЕНИЯ ---
  const activeLang =
    i18n.resolvedLanguage ||
    i18n.language ||
    (typeof navigator !== "undefined" ? navigator.language : "en");
  const isRu = activeLang.toLowerCase().startsWith("ru");

  const uiText = useMemo(
    () =>
      isRu
        ? {
            pipTitle: "Картинка в картинке",
            record: "Записать",
            recording: "Идёт запись",
            starting: "Подключение...",
            stopping: "Остановка...",
            videoAndAudio: "Видео + Аудио",
            audioOnly: "Только Аудио",
            recordError: "Ошибка сервера при старте записи",
            defaultParticipant: "Участник",
            defaultRoomName: "Конференция",
            recentlySpoke: "Недавно говорил(а)",
            speakingNow: "● Говорит сейчас",
            micMuted: "Микрофон выключен",
            participantsCount: "Участников",
            mediaSessionArtist: "В звонке",
            fallbackInitial: "У",
          }
        : {
            pipTitle: "Picture-in-Picture",
            record: "Record",
            recording: "Recording",
            starting: "Starting...",
            stopping: "Stopping...",
            videoAndAudio: "Video + Audio",
            audioOnly: "Audio only",
            recordError: "Server error starting recording",
            defaultParticipant: "Participant",
            defaultRoomName: "Conference",
            recentlySpoke: "Recently spoke",
            speakingNow: "● Speaking now",
            micMuted: "Microphone muted",
            participantsCount: "Participants",
            mediaSessionArtist: "In call",
            fallbackInitial: "U",
          },
    [isRu],
  );

  const uiTextRef = useRef(uiText);
  uiTextRef.current = uiText;

  const allConnections = useBehavior(vm.allConnections$);
  const livekitRoom = allConnections.getConnections()[0]?.livekitRoom;

  // --- УМНАЯ СИНХРОНИЗАЦИЯ ЗАПИСИ (LIVEKIT EGRESS) ---
  const [isGlobalRecording, setIsGlobalRecording] = useState(false);
  const [isRecordingStarting, setIsRecordingStarting] = useState(false);
  const [showRecordMenu, setShowRecordMenu] = useState(false);

  useEffect(() => {
    if (!livekitRoom) return;

    setIsGlobalRecording(livekitRoom.isRecording);

    const handleRecordingChange = (isRecording: boolean) => {
      setIsGlobalRecording(isRecording);
      setIsRecordingStarting(false);
    };

    livekitRoom.on(RoomEvent.RecordingStatusChanged, handleRecordingChange);

    return () => {
      livekitRoom.off(RoomEvent.RecordingStatusChanged, handleRecordingChange);
    };
  }, [livekitRoom]);

  const toggleRecording = async (type: "video" | "audio" | any = "video") => {
    const recordType = typeof type === "string" ? type : "video";

    if (!livekitRoom || isRecordingStarting) return;

    const userId = livekitRoom.localParticipant.identity;
    const roomId = livekitRoom.name;

    setIsRecordingStarting(true);
    setShowRecordMenu(false);

    try {
      const endpoint = isGlobalRecording ? "stop" : "start";

      const res = await fetch(`${BACKEND_URL}/api/recording/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, userId, type: recordType }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setIsRecordingStarting(false);
        alert(data.error || uiTextRef.current.recordError);
        return;
      }
    } catch (e) {
      console.error("Ошибка запроса записи:", e);
      setIsRecordingStarting(false);
    }
  };

  useWakeLock();

  const containerRef1 = useRef<HTMLDivElement | null>(null);
  const [containerRef2, bounds] = useMeasure();
  const containerRef = useMergedRefs(containerRef1, containerRef2);

  const { showControls, header: headerStyle } = useUrlParams();

  const muteAllAudio = useBehavior(muteAllAudio$);
  const toggleAudio = useBehavior(muteStates.audio.toggle$);
  const toggleVideo = useBehavior(muteStates.video.toggle$);
  const setAudioEnabled = useBehavior(muteStates.audio.setEnabled$);
  const audioEnabled = useBehavior(muteStates.audio.enabled$);
  const videoEnabled = useBehavior(muteStates.video.enabled$);

  useCallViewKeyboardShortcuts(
    toggleAudio,
    toggleVideo,
    setAudioEnabled,
    (reaction) => void sendReaction(reaction),
    () => void toggleRaisedHand(),
  );

  const ringingVm = useBehavior(vm.ringingVm$);
  const audioParticipants = useBehavior(vm.livekitRoomItems$);
  const participantCount = useBehavior(vm.participantCount$);
  const reconnecting = useBehavior(vm.reconnecting$);
  const layout = useBehavior(vm.layout$);
  const edgeToEdge = useBehavior(vm.edgeToEdge$);
  const overflowing = useBehavior(vm.overflowing$);
  const showNameTags = useBehavior(vm.showNameTags$);
  const showHeader = useBehavior(vm.showHeader$);
  const settingsOpen = useBehavior(vm.settingsOpen$);
  const setSettingsOpen = useBehavior(vm.setSettingsOpen$);
  const earpieceMode = useBehavior(vm.earpieceMode$);
  const audioOutputSwitcher = useBehavior(vm.audioOutputSwitcher$);

  // --- УМНАЯ КАРТИНКА В КАРТИНКЕ (АКТИВНЫЙ СПИКЕР + ФОНОВЫЙ WORKER) ---
  const pipVideoRef = useRef<ExtendedVideoElement | null>(null);
  const docPipWindowRef = useRef<Window | null>(null);
  const [isPipActive, setIsPipActive] = useState(false);

  const lastSpeakerIdentityRef = useRef<string | null>(null);
  const livekitRoomRef = useRef(livekitRoom);
  livekitRoomRef.current = livekitRoom;
  const participantCountRef = useRef(participantCount);
  participantCountRef.current = participantCount;
  const roomNameRef = useRef(matrixInfo.roomName);
  roomNameRef.current = matrixInfo.roomName;
  const localDisplayNameRef = useRef(matrixInfo.displayName);
  localDisplayNameRef.current = matrixInfo.displayName;

  useEffect(() => {
    if (!livekitRoom) return;

    const onActiveSpeakersChanged = (speakers: Participant[]): void => {
      if (speakers.length === 0) return;
      const preferred = speakers.find((p) => !p.isLocal) ?? speakers[0];
      if (preferred?.identity) {
        lastSpeakerIdentityRef.current = preferred.identity;
      }
    };

    livekitRoom.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
    return (): void => {
      livekitRoom.off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
    };
  }, [livekitRoom]);

  const resolveParticipantName = useCallback(
    (participant: Participant | undefined): string => {
      const fallbackName = uiTextRef.current.defaultParticipant;
      if (!participant) return localDisplayNameRef.current || fallbackName;
      if (participant.isLocal && localDisplayNameRef.current) {
        return localDisplayNameRef.current;
      }
      if (participant.name && !participant.name.startsWith("@")) {
        return participant.name;
      }

      const id = participant.identity || "";
      if (id.startsWith("@")) {
        const parts = id.split(":");
        const matrixUserId =
          parts.length >= 2 ? `${parts[0]}:${parts[1]}` : id;
        const member = matrixRoom.getMember(matrixUserId);
        if (member?.rawDisplayName) return member.rawDisplayName;
        if (member?.name) return member.name;
        return parts[0].slice(1);
      }

      return id.split(":")[0] || fallbackName;
    },
    [matrixRoom],
  );

  const findParticipantVideoElement = useCallback(
    (participant: Participant | undefined): HTMLVideoElement | null => {
      if (participant) {
        const pubs = (
          participant as unknown as {
            videoTrackPublications?: Map<
              string,
              {
                isMuted?: boolean;
                track?: { attachedElements?: HTMLMediaElement[] };
              }
            >;
          }
        ).videoTrackPublications;

        if (pubs) {
          for (const pub of pubs.values()) {
            if (!pub.isMuted && pub.track?.attachedElements) {
              for (const el of pub.track.attachedElements) {
                if (
                  el instanceof HTMLVideoElement &&
                  el !== pipVideoRef.current &&
                  el.readyState >= 2 &&
                  el.videoWidth > 0
                ) {
                  return el;
                }
              }
            }
          }
        }
      }

      const container = containerRef1.current;
      if (!container) return null;
      const videos = Array.from(container.querySelectorAll("video")).filter(
        (v) =>
          v !== pipVideoRef.current &&
          v.readyState >= 2 &&
          v.videoWidth > 0 &&
          !v.paused,
      );
      if (videos.length === 0) return null;

      videos.sort((a, b) => {
        const rectA = a.getBoundingClientRect();
        const rectB = b.getBoundingClientRect();
        return rectB.width * rectB.height - rectA.width * rectA.height;
      });
      return videos[0];
    },
    [],
  );

  // Отрисовка плитки спикера через Web Worker (не замирает при сворачивании вкладки/браузера)
  useEffect(() => {
    const pipVideo = pipVideoRef.current;
    if (!pipVideo) return;

    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext("2d");

    let pulsePhase = 0;

    const drawSpeakerTile = (): void => {
      if (!ctx) return;
      pulsePhase += 0.25;
      const labels = uiTextRef.current;

      const room = livekitRoomRef.current;
      let targetParticipant: Participant | undefined;

      if (room) {
        const remotes = Array.from(room.remoteParticipants.values());
        const allParticipants: Participant[] = [
          ...remotes,
          room.localParticipant,
        ];

        const speakingNow =
          remotes.find((p) => p.isSpeaking) ??
          (room.localParticipant.isSpeaking
            ? room.localParticipant
            : undefined);

        if (speakingNow) {
          targetParticipant = speakingNow;
          lastSpeakerIdentityRef.current = speakingNow.identity;
        } else if (lastSpeakerIdentityRef.current) {
          targetParticipant = allParticipants.find(
            (p) => p.identity === lastSpeakerIdentityRef.current,
          );
        }

        if (!targetParticipant) {
          targetParticipant = remotes[0] ?? room.localParticipant;
        }
      }

      const speakerName = resolveParticipantName(targetParticipant);
      const isSpeaking = Boolean(targetParticipant?.isSpeaking);
      const isMicEnabled = Boolean(targetParticipant?.isMicrophoneEnabled);
      const liveVideo = findParticipantVideoElement(targetParticipant);

      ctx.fillStyle = "#14161c";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (liveVideo && liveVideo.videoWidth > 0 && liveVideo.videoHeight > 0) {
        const vw = liveVideo.videoWidth;
        const vh = liveVideo.videoHeight;
        const scale = Math.max(canvas.width / vw, canvas.height / vh);
        const dw = vw * scale;
        const dh = vh * scale;
        const dx = (canvas.width - dw) / 2;
        const dy = (canvas.height - dh) / 2;

        ctx.drawImage(liveVideo, dx, dy, dw, dh);

        const grad = ctx.createLinearGradient(
          0,
          canvas.height - 90,
          0,
          canvas.height,
        );
        grad.addColorStop(0, "rgba(0, 0, 0, 0)");
        grad.addColorStop(1, "rgba(0, 0, 0, 0.8)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, canvas.height - 90, canvas.width, 90);

        if (isSpeaking) {
          ctx.strokeStyle = "#34c759";
          ctx.lineWidth = 6;
          ctx.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);
        }

        ctx.fillStyle = "#ffffff";
        ctx.font = "600 22px -apple-system, BlinkMacSystemFont, sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(speakerName, 24, canvas.height - 24);
        return;
      }

      // --- ПЛИТКА АКТИВНОГО / ПОСЛЕДНЕГО СПИКЕРА (КОГДА КАМЕРЫ ВЫКЛЮЧЕНЫ) ---
      ctx.fillStyle = "#1f222b";
      ctx.beginPath();
      ctx.roundRect(20, 20, canvas.width - 40, canvas.height - 40, 20);
      ctx.fill();

      if (isSpeaking) {
        ctx.strokeStyle = "#34c759";
        ctx.lineWidth = 4;
        ctx.stroke();
      } else {
        ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      const centerX = canvas.width / 2;
      const avatarY = 140;
      const avatarRadius = 54;

      if (isSpeaking) {
        const pulseRadius = avatarRadius + 10 + Math.sin(pulsePhase) * 6;
        ctx.beginPath();
        ctx.arc(centerX, avatarY, pulseRadius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(52, 199, 89, 0.28)";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(centerX, avatarY, avatarRadius + 4, 0, Math.PI * 2);
        ctx.strokeStyle = "#34c759";
        ctx.lineWidth = 3.5;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(centerX, avatarY, avatarRadius, 0, Math.PI * 2);
      ctx.fillStyle = getAvatarColor(speakerName);
      ctx.fill();

      const initial = (
        speakerName.trim()[0] || labels.fallbackInitial
      ).toUpperCase();
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 46px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(initial, centerX, avatarY + 2);

      ctx.textBaseline = "alphabetic";
      ctx.fillStyle = "#ffffff";
      ctx.font = "600 28px -apple-system, BlinkMacSystemFont, sans-serif";
      const displayTitle =
        speakerName.length > 24 ? speakerName.slice(0, 24) + "…" : speakerName;
      ctx.fillText(displayTitle, centerX, 245);

      let statusText = labels.recentlySpoke;
      let statusColor = "#9ea4b0";
      if (isSpeaking) {
        statusText = labels.speakingNow;
        statusColor = "#34c759";
      } else if (!isMicEnabled) {
        statusText = labels.micMuted;
        statusColor = "#ff6b6b";
      }

      ctx.fillStyle = statusColor;
      ctx.font = "500 18px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.fillText(statusText, centerX, 280);

      ctx.fillStyle = "rgba(255, 255, 255, 0.45)";
      ctx.font = "500 14px -apple-system, BlinkMacSystemFont, sans-serif";
      ctx.textAlign = "left";
      const headerLabel = `${roomNameRef.current || labels.defaultRoomName} • ${labels.participantsCount}: ${participantCountRef.current}`;
      ctx.fillText(headerLabel, 40, 50);
    };

    try {
      if (typeof canvas.captureStream === "function") {
        const stream = canvas.captureStream(15);
        pipVideo.srcObject = stream;
      }
    } catch (e) {
      logger.debug("Canvas captureStream error:", e);
    }

    drawSpeakerTile();

    let worker: Worker | null = null;
    let fallbackInterval: number | null = null;
    try {
      const blob = new Blob(["setInterval(() => postMessage(0), 120);"], {
        type: "application/javascript",
      });
      worker = new Worker(URL.createObjectURL(blob));
      worker.onmessage = (): void => drawSpeakerTile();
    } catch {
      fallbackInterval = window.setInterval(drawSpeakerTile, 120);
    }

    pipVideo.autoPictureInPicture = true;
    pipVideo.setAttribute("autopictureinpicture", "");
    pipVideo.disablePictureInPicture = false;
    pipVideo.removeAttribute("disablepictureinpicture");
    pipVideo.play().catch(() => {});

    return (): void => {
      worker?.terminate();
      if (fallbackInterval !== null) window.clearInterval(fallbackInterval);
    };
  }, [resolveParticipantName, findParticipantVideoElement, logger]);

  // Переключение PiP (с поддержкой живого WebRTC-видео на телефонах и Document PiP в новых браузерах)
  const togglePiP = useCallback(async (): Promise<void> => {
    if (docPipWindowRef.current && !docPipWindowRef.current.closed) {
      docPipWindowRef.current.close();
      docPipWindowRef.current = null;
      setIsPipActive(false);
      return;
    }

    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture().catch(() => {});
      setIsPipActive(false);
      return;
    }

    const room = livekitRoomRef.current;
    let speakingParticipant: Participant | undefined;
    if (room) {
      const remotes = Array.from(room.remoteParticipants.values());
      speakingParticipant =
        remotes.find((p) => p.isSpeaking) ??
        remotes[0] ??
        room.localParticipant;
    }

    const liveWebrtcVideo = findParticipantVideoElement(
      speakingParticipant,
    ) as ExtendedVideoElement | null;

    const targetVideo = liveWebrtcVideo ?? pipVideoRef.current;
    if (!targetVideo) return;

    try {
      targetVideo.disablePictureInPicture = false;
      targetVideo.removeAttribute("disablepictureinpicture");
      if (targetVideo.paused) {
        await targetVideo.play().catch(() => {});
      }

      if (
        typeof targetVideo.webkitSupportsPresentationMode === "function" &&
        typeof targetVideo.webkitSetPresentationMode === "function" &&
        targetVideo.webkitSupportsPresentationMode("picture-in-picture")
      ) {
        if (targetVideo.webkitPresentationMode === "picture-in-picture") {
          targetVideo.webkitSetPresentationMode("inline");
          setIsPipActive(false);
        } else {
          targetVideo.webkitSetPresentationMode("picture-in-picture");
          setIsPipActive(true);
        }
        return;
      }

      if (
        document.pictureInPictureEnabled &&
        typeof targetVideo.requestPictureInPicture === "function"
      ) {
        await targetVideo.requestPictureInPicture();
        setIsPipActive(true);
        return;
      }

      const docPip = (
        window as unknown as {
          documentPictureInPicture?: {
            requestWindow: (opts: {
              width: number;
              height: number;
            }) => Promise<Window>;
          };
        }
      ).documentPictureInPicture;

      if (docPip && pipVideoRef.current?.srcObject) {
        const pipWin = await docPip.requestWindow({ width: 360, height: 202 });
        docPipWindowRef.current = pipWin;
        pipWin.document.body.style.margin = "0";
        pipWin.document.body.style.background = "#14161c";
        const mirrorVideo = pipWin.document.createElement("video");
        mirrorVideo.srcObject = pipVideoRef.current.srcObject;
        mirrorVideo.autoplay = true;
        mirrorVideo.muted = true;
        mirrorVideo.playsInline = true;
        mirrorVideo.style.width = "100%";
        mirrorVideo.style.height = "100%";
        mirrorVideo.style.objectFit = "cover";
        pipWin.document.body.appendChild(mirrorVideo);
        void mirrorVideo.play();
        setIsPipActive(true);
        pipWin.addEventListener("pagehide", () => {
          docPipWindowRef.current = null;
          setIsPipActive(false);
        });
      }
    } catch (err) {
      logger.debug("Toggle PiP error:", err);
    }
  }, [findParticipantVideoElement, logger]);

  useEffect(() => {
    const onEnterPiP = (): void => setIsPipActive(true);
    const onLeavePiP = (): void => setIsPipActive(false);

    document.addEventListener("enterpictureinpicture", onEnterPiP, true);
    document.addEventListener("leavepictureinpicture", onLeavePiP, true);

    return (): void => {
      document.removeEventListener("enterpictureinpicture", onEnterPiP, true);
      document.removeEventListener("leavepictureinpicture", onLeavePiP, true);
    };
  }, []);

  // Автоматический PiP при переключении вкладки в Chrome (через MediaSession API)
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession as ExtendedMediaSession;

    try {
      ms.metadata = new MediaMetadata({
        title: matrixInfo.roomName || uiText.defaultRoomName,
        artist: uiText.mediaSessionArtist,
      });
      ms.playbackState = "playing";
    } catch {}

    const safeSetHandler = (
      action: string,
      handler: MediaSessionActionHandler | null,
    ): void => {
      try {
        ms.setActionHandler(action as MediaSessionAction, handler);
      } catch {}
    };

    safeSetHandler("enterpictureinpicture", () => {
      void togglePiP();
    });
    safeSetHandler("togglemicrophone", () => {
      toggleAudio();
    });
    safeSetHandler("togglecamera", () => {
      toggleVideo();
    });
    safeSetHandler("hangup", () => {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
      vm.hangup();
    });

    return (): void => {
      safeSetHandler("enterpictureinpicture", null);
      safeSetHandler("togglemicrophone", null);
      safeSetHandler("togglecamera", null);
      safeSetHandler("hangup", null);
    };
  }, [togglePiP, toggleAudio, toggleVideo, vm, matrixInfo.roomName, uiText]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession as ExtendedMediaSession;
    try {
      ms.setMicrophoneActive?.(audioEnabled);
      ms.setCameraActive?.(videoEnabled);
    } catch {}
  }, [audioEnabled, videoEnabled]);

  const fatalCallError = useBehavior(vm.fatalError$);
  if (fatalCallError) {
    logger.debug("fatalCallError stop rendering", fatalCallError);
    throw fatalCallError;
  }

  const onViewPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      if (
        e.pointerType === "touch" &&
        (e.target as Element).closest?.("button, input") === null
      )
        vm.tapScreen();
    },
    [vm],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (e.pointerType === "mouse") vm.hoverScreen();
    },
    [vm],
  );
  const onPointerOut = useCallback(() => vm.unhoverScreen(), [vm]);

  const [settingsTab, setSettingsTab] = useState(defaultSettingsTab);

  const openProfile = useMemo(
    () =>
      widget === null
        ? (): void => {
            setSettingsTab("profile");
            setSettingsOpen(true);
          }
        : null,
    [setSettingsTab, setSettingsOpen],
  );

  const [headerRef, headerBounds] = useMeasure();
  const [footerRef, footerBounds] = useMeasure();

  const gridBounds = useMemo(
    () => ({
      width: bounds.width,
      height:
        bounds.height -
        (edgeToEdge ? 0 : headerBounds.height + footerBounds.height),
    }),
    [
      bounds.width,
      bounds.height,
      headerBounds.height,
      footerBounds.height,
      edgeToEdge,
    ],
  );
  const gridBoundsObservable$ = useObservable(
    (inputs$) => inputs$.pipe(map(([gridBounds]) => gridBounds)),
    [gridBounds],
  );

  useAppBarHidden(!showHeader);
  useAppBarSubtitle(
    ringingVm && vm.ringingStatusLocation === "app_bar" && (
      <RingingStatus vm={ringingVm} />
    ),
  );

  let header: ReactNode = null;
  switch (headerStyle) {
    case HeaderStyle.AppBar: {
      break;
    }
    case HeaderStyle.None:
      header = showHeader && (
        <div
          className={classNames(styles.header, styles.filler)}
          ref={headerRef}
        />
      );
      break;
    case HeaderStyle.Standard:
      header = (
        <Header
          className={classNames(styles.header, {
            [styles.overlay]: edgeToEdge,
            [styles.hidden]: !showHeader,
          })}
          ref={headerRef}
          disconnectedBanner={false}
        >
          <LeftNav>
            <RoomHeaderInfo
              id={matrixInfo.roomId}
              name={matrixInfo.roomName}
              avatarUrl={matrixInfo.roomAvatar}
              encrypted={matrixInfo.e2eeSystem.kind !== E2eeType.NONE}
              participantCount={participantCount}
            />
          </LeftNav>
          <RightNav>
            {/* КНОПКА КАРТИНКА В КАРТИНКЕ (PiP) */}
            <button
              onClick={() => void togglePiP()}
              title={uiText.pipTitle}
              aria-label={uiText.pipTitle}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 10px",
                borderRadius: "8px",
                border: isPipActive
                  ? "1px solid rgba(52, 199, 89, 0.6)"
                  : "1px solid rgba(255, 255, 255, 0.2)",
                backgroundColor: isPipActive
                  ? "rgba(52, 199, 89, 0.15)"
                  : "rgba(0, 0, 0, 0.2)",
                color: isPipActive ? "#34c759" : "#fff",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <rect
                  x="12"
                  y="9"
                  width="8"
                  height="6"
                  rx="1"
                  fill="currentColor"
                />
              </svg>
            </button>

            {/* ВЫПАДАЮЩЕЕ МЕНЮ ЗАПИСИ */}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => {
                  if (isGlobalRecording) {
                    toggleRecording();
                  } else {
                    setShowRecordMenu(!showRecordMenu);
                  }
                }}
                disabled={isRecordingStarting}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: isGlobalRecording
                    ? "1px solid rgba(255, 59, 48, 0.5)"
                    : "1px solid rgba(255, 255, 255, 0.2)",
                  backgroundColor: isGlobalRecording
                    ? "rgba(255, 59, 48, 0.1)"
                    : "rgba(0, 0, 0, 0.2)",
                  color: isGlobalRecording ? "#ff3b30" : "#fff",
                  cursor: isRecordingStarting ? "wait" : "pointer",
                  fontWeight: 600,
                  fontSize: "14px",
                  transition: "all 0.2s ease",
                  opacity: isRecordingStarting ? 0.7 : 1,
                }}
              >
                {isRecordingStarting ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ animation: "spin 1s linear infinite" }}
                  >
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                  </svg>
                ) : (
                  <div
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: isGlobalRecording
                        ? "#ff3b30"
                        : "currentColor",
                      boxShadow: isGlobalRecording ? "0 0 8px #ff3b30" : "none",
                      animation: isGlobalRecording
                        ? "pulse 2s infinite"
                        : "none",
                    }}
                  />
                )}
                {isRecordingStarting
                  ? isGlobalRecording
                    ? uiText.stopping
                    : uiText.starting
                  : isGlobalRecording
                    ? uiText.recording
                    : uiText.record}
              </button>

              {showRecordMenu && !isGlobalRecording && !isRecordingStarting && (
                <div
                  style={{
                    position: "absolute",
                    top: "110%",
                    right: 0,
                    background: "#1e1e1e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    padding: "8px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                    minWidth: "180px",
                  }}
                >
                  <button
                    onClick={() => toggleRecording("video")}
                    style={{
                      background: "transparent",
                      color: "white",
                      border: "none",
                      padding: "10px",
                      textAlign: "left",
                      cursor: "pointer",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background = "#333")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {uiText.videoAndAudio}
                  </button>
                  <button
                    onClick={() => toggleRecording("audio")}
                    style={{
                      background: "transparent",
                      color: "white",
                      border: "none",
                      padding: "10px",
                      textAlign: "left",
                      cursor: "pointer",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background = "#333")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {uiText.audioOnly}
                  </button>
                </div>
              )}
            </div>

            {showControls && onShareClick !== null && (
              <InviteButton className={styles.invite} onClick={onShareClick} />
            )}
          </RightNav>
        </Header>
      );
      break;
  }

  const onDismissReconnectingToast = useCallback(() => {}, []);
  const reconnectingToast = (
    <>
      <div
        className={classNames(overlayStyles.bg, overlayStyles.animate)}
        data-state={reconnecting ? "open" : "closed"}
      />
      <Toast
        onDismiss={onDismissReconnectingToast}
        open={reconnecting}
        modal={false}
      >
        {t("common.reconnecting")}
      </Toast>
    </>
  );

  const earpieceOverlay = (
    <EarpieceOverlay
      show={earpieceMode && !reconnecting}
      onBackToVideoPressed={audioOutputSwitcher?.switch}
    />
  );

  const contentObscured = reconnecting || earpieceMode;

  const Tile = useMemo(
    () =>
      function Tile({
        ref,
        className,
        style,
        targetWidth,
        targetHeight,
        model,
      }: TileProps<TileViewModel, HTMLDivElement>): ReactNode {
        const spotlightExpanded = useBehavior(vm.spotlightExpanded$);
        const onToggleExpanded = useBehavior(vm.toggleSpotlightExpanded$);
        const showSpotlightIndicators = useBehavior(
          vm.showSpotlightIndicators$,
        );
        const showSpeakingIndicators = useBehavior(vm.showSpeakingIndicators$);
        const showNameTags = useBehavior(vm.showNameTags$);
        const showRingingStatus = vm.ringingStatusLocation === "tile";
        const showOutline = useBehavior(
          model instanceof GridTileViewModel
            ? model.showOutline$
            : constant(false),
        );

        return model instanceof GridTileViewModel ? (
          <GridTile
            ref={ref}
            vm={model}
            onOpenProfile={openProfile}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
            className={classNames(className, styles.tile)}
            style={style}
            showSpeakingIndicators={showSpeakingIndicators}
            showNameTags={showNameTags}
            showRingingStatus={showRingingStatus}
            showOutline={showOutline}
            focusable={!contentObscured}
          />
        ) : (
          <SpotlightTile
            ref={ref}
            vm={model}
            expanded={spotlightExpanded}
            onToggleExpanded={onToggleExpanded}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
            showIndicators={showSpotlightIndicators}
            showNameTags={showNameTags}
            showRingingStatus={showRingingStatus}
            focusable={!contentObscured}
            className={classNames(className, styles.tile)}
            itemClassName={styles.spotlightItem}
            style={style}
          />
        );
      },
    [vm, openProfile, contentObscured],
  );

  const layouts = useMemo(() => {
    const inputs = { minBounds$: gridBoundsObservable$ };
    return {
      grid: makeGridLayout(inputs),
      "spotlight-landscape": makeSpotlightLandscapeLayout(inputs),
      "spotlight-portrait": makeSpotlightPortraitLayout(inputs),
      "spotlight-expanded": makeSpotlightExpandedLayout(inputs),
      "one-on-one-landscape": makeOneOnOneLandscapeLayout(inputs),
      "one-on-one-portrait": makeOneOnOnePortraitLayout(inputs),
    };
  }, [gridBoundsObservable$]);

  const showFooter = useBehavior(footerVm.showFooter$);
  const renderContent = (): JSX.Element => {
    if (layout.type === "pip") {
      return (
        <SpotlightTile
          className={styles.tile}
          itemClassName={styles.spotlightItem}
          data-maximised
          vm={layout.spotlight}
          expanded
          onToggleExpanded={null}
          targetWidth={gridBounds.width}
          targetHeight={gridBounds.height}
          showIndicators={false}
          showNameTags={showNameTags}
          showRingingStatus={vm.ringingStatusLocation === "tile"}
          focusable={!contentObscured}
          aria-hidden={contentObscured}
        />
      );
    }

    const layers = layouts[layout.type] as CallLayoutOutputs<Layout>;
    const fixedGrid = (
      <Grid
        key="fixed"
        className={styles.fixedGrid}
        style={{
          insetBlockStart: edgeToEdge ? 0 : bounds.top + headerBounds.height,
          height: edgeToEdge ? "100%" : gridBounds.height,
          "--call-view-safe-area-inset-top":
            edgeToEdge && headerStyle !== HeaderStyle.None && showHeader
              ? headerStyle === HeaderStyle.AppBar
                ? `${bounds.top}px`
                : `calc(env(safe-area-inset-top) + ${headerBounds.height}px)`
              : undefined,
          "--call-view-safe-area-inset-bottom":
            edgeToEdge && showFooter
              ? `calc(env(safe-area-inset-bottom) + ${footerBounds.height}px)`
              : undefined,
        }}
        model={layout}
        Layout={layers.fixed}
        Tile={Tile}
        aria-hidden={contentObscured}
      />
    );
    const scrollingGrid = (
      <Grid
        key="scrolling"
        className={styles.scrollingGrid}
        model={layout}
        Layout={layers.scrolling}
        Tile={Tile}
        aria-hidden={contentObscured}
      />
    );

    switch (layers.foreground) {
      case "fixed":
        return (
          <>
            {scrollingGrid}
            {fixedGrid}
          </>
        );
      case "scrolling":
        return (
          <>
            {fixedGrid}
            {scrollingGrid}
          </>
        );
    }
  };

  const rageshakeRequestModalProps = useRageshakeRequestModal(
    matrixRoom.roomId,
  );

  useAppBarSecondaryButton(
    <SettingsIconButton
      key="settings"
      onClick={() => setSettingsOpen(true)}
      data-testid="settings-app-bar"
    />,
  );

  const footer = footerVm !== null && (
    <CallFooter className={styles.footer} ref={footerRef} vm={footerVm} />
  );

  return (
    <div
      className={classNames(styles.inRoom, {
        [styles.overflowing]: overflowing,
      })}
      ref={containerRef}
      onPointerUp={onViewPointerUp}
      onPointerMove={onPointerMove}
      onPointerOut={onPointerOut}
    >
      {header}
      {audioParticipants.map(({ livekitRoom, url, participants }) => (
        <LivekitRoomAudioRenderer
          key={url}
          url={url}
          livekitRoom={livekitRoom}
          validIdentities={participants}
          muted={muteAllAudio}
        />
      ))}
      {renderContent()}
      <CallEventAudioRenderer vm={vm} muted={muteAllAudio} />
      <ReactionsAudioRenderer vm={vm} muted={muteAllAudio} />
      <RingingAudioRenderer vm={ringingVm} muted={muteAllAudio} />
      {reconnectingToast}
      {earpieceOverlay}
      <ReactionsOverlay vm={vm} />

      {/* Полностью невидимый элемент для PiP (без полупрозрачного окна в фоне) */}
      <video
        ref={pipVideoRef}
        muted
        playsInline
        autoPlay
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          width: "2px",
          height: "2px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      />

      {/* ИДЕАЛЬНАЯ ИНЪЕКЦИЯ ЧАТА С КОНТЕКСТОМ */}
      {livekitRoom && (
        <RoomContext.Provider value={livekitRoom}>
          <ChatWidget displayName={matrixInfo.displayName} />
        </RoomContext.Provider>
      )}

      {footer}
      {layout.type !== "pip" && (
        <>
          <RageshakeRequestModal {...rageshakeRequestModalProps} />
          <SettingsModal
            client={client}
            roomId={matrixRoom.roomId}
            open={settingsOpen}
            onDismiss={(): void => setSettingsOpen(false)}
            tab={settingsTab}
            onTabChange={setSettingsTab}
            livekitRooms={allConnections
              .getConnections()
              .map((connectionItem) => ({
                room: connectionItem.livekitRoom,
                livekitAlias: connectionItem.livekitAlias,
                isLocal: undefined,
                url: connectionItem.transport.livekit_service_url,
              }))}
          />
        </>
      )}
    </div>
  );
};
